import { DataTable, type Column } from '@app/layouts/DataTable';
import { Badge } from '@shared/ui/Badge';
import { formatDate } from '@shared/lib/format-date';
import { statusTone, humanizeStatus } from '@features/users/model/user.types';
import {
  describeRecency,
  formatCount,
  formatMoney,
  tierLabel,
  tierTone,
  type UserEngagement,
} from '@features/engagement/model/engagement.types';

interface EngagementTableProps {
  users: UserEngagement[];
  loading?: boolean;
  error?: string | null;
}

/** Per-user usage: how much they built, and when they were last here. */
export function EngagementTable({ users, loading = false, error = null }: EngagementTableProps) {
  const columns: Column<UserEngagement>[] = [
    {
      key: 'user',
      header: 'User',
      alwaysVisible: true,
      sortValue: (user) => user.name ?? user.email,
      render: (user) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-slate-900">
            {user.name ?? user.email ?? 'Unnamed user'}
          </div>
          {user.name && user.email && (
            <div className="truncate text-xs text-slate-400">{user.email}</div>
          )}
        </div>
      ),
    },
    {
      key: 'tier',
      header: 'Engagement',
      sortValue: (user) => user.tier,
      render: (user) => <Badge tone={tierTone(user.tier)}>{tierLabel(user.tier)}</Badge>,
    },
    {
      key: 'subscription',
      header: 'Subscription',
      sortValue: (user) => user.subscriptionStatus,
      render: (user) =>
        user.subscriptionStatus ? (
          <div className="min-w-0">
            <Badge tone={statusTone(user.subscriptionStatus)}>
              {humanizeStatus(user.subscriptionStatus)}
            </Badge>
            {user.planName && (
              <div className="mt-0.5 truncate text-xs text-slate-400">{user.planName}</div>
            )}
          </div>
        ) : (
          <span className="text-slate-400">None</span>
        ),
    },
    {
      key: 'budgets',
      header: 'Budgets',
      align: 'right',
      sortValue: (user) => user.budgets,
      render: (user) => (
        <div className="whitespace-nowrap">
          <span className={user.budgets > 0 ? 'tabular-nums text-slate-900' : 'text-slate-300'}>
            {user.budgets > 0 ? user.budgets : '—'}
          </span>
          {/* An empty budget is the whole "created and left" signal, so it is
              called out rather than buried in the total. */}
          {user.emptyBudgets > 0 && (
            <span className="ml-1 text-xs text-amber-700">{user.emptyBudgets} empty</span>
          )}
        </div>
      ),
    },
    {
      key: 'lines',
      header: 'Lines',
      title: 'Budget lines',
      align: 'right',
      sortValue: (user) => user.lines,
      render: (user) => (
        <span className={user.lines > 0 ? 'tabular-nums text-slate-900' : 'text-slate-300'}>
          {user.lines > 0 ? formatCount(user.lines) : '—'}
        </span>
      ),
    },
    {
      key: 'budgetValue',
      header: 'Budget value',
      align: 'right',
      sortValue: (user) => user.budgetValue,
      render: (user) =>
        user.budgetValue > 0 ? (
          <span
            className="whitespace-nowrap tabular-nums text-slate-900"
            title={`${user.budgetValue.toLocaleString('en-US')} ${user.currency ?? ''}`.trim()}
          >
            {formatMoney(user.budgetValue, user.currency)}
            {/* Budgets in the user's other currencies are not in this figure —
                there are no exchange rates here to fold them in with. */}
            {user.hasMixedCurrencies && (
              <span className="ml-1 text-xs text-slate-400" title="Also has budgets in another currency">
                +
              </span>
            )}
          </span>
        ) : (
          <span className="text-slate-300">—</span>
        ),
    },
    {
      key: 'accounts',
      header: 'Accounts',
      align: 'right',
      sortValue: (user) => user.accounts,
      render: (user) => (
        <span className={user.accounts > 0 ? 'tabular-nums text-slate-600' : 'text-slate-300'}>
          {user.accounts > 0 ? formatCount(user.accounts) : '—'}
        </span>
      ),
    },
    {
      key: 'categories',
      header: 'Categories',
      align: 'right',
      sortValue: (user) => user.categories,
      render: (user) => (
        <span className={user.categories > 0 ? 'tabular-nums text-slate-600' : 'text-slate-300'}>
          {user.categories > 0 ? formatCount(user.categories) : '—'}
        </span>
      ),
    },
    {
      key: 'edits',
      header: 'Edits',
      title: 'Field changes made',
      align: 'right',
      sortValue: (user) => user.edits,
      render: (user) => (
        <span className={user.edits > 0 ? 'tabular-nums text-slate-600' : 'text-slate-300'}>
          {user.edits > 0 ? formatCount(user.edits) : '—'}
        </span>
      ),
    },
    {
      key: 'lastActivity',
      header: 'Last activity',
      align: 'right',
      // Sorted on the day count, not the phrasing — "Yesterday" would otherwise
      // sort beside "Never".
      sortValue: (user) => user.daysSinceActivity,
      render: (user) => (
        <span
          className={
            user.daysSinceActivity === null
              ? 'whitespace-nowrap text-slate-300'
              : 'whitespace-nowrap text-slate-600'
          }
          title={user.lastActivityAt ? formatDate(user.lastActivityAt) : undefined}
        >
          {describeRecency(user.daysSinceActivity)}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Signed up',
      align: 'right',
      sortValue: (user) => user.createdAt,
      render: (user) => (
        <span className="whitespace-nowrap tabular-nums text-slate-500">
          {formatDate(user.createdAt)}
        </span>
      ),
    },
  ];

  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white">
      <DataTable
        columns={columns}
        rows={users}
        rowKey={(user) => user.userId}
        isLoading={loading}
        isError={Boolean(error)}
        errorMessage={error ?? undefined}
        loadingMessage="Building the usage report…"
        emptyMessage="No users match these filters."
        columnToggle={false}
        dense
      />
    </div>
  );
}
